import { FC, useEffect, useState } from 'react'
import {
	Table,
	Button,
	Form,
	Input,
	Modal,
	Space,
	Tag,
	Typography,
	message
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
	CarrierType,
	createCarrierType,
	fetchCarrierTypes
} from '../api/updates'

const { Title } = Typography

const UpdatesCarrierTypes: FC = () => {
	const [types, setTypes] = useState<CarrierType[]>([])
	const [loading, setLoading] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [form] = Form.useForm()

	const load = async () => {
		setLoading(true)
		try {
			const data = await fetchCarrierTypes()
			setTypes(data)
		} catch {
			message.error('Failed to load carrier types')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		load()
	}, [])

	const handleCreate = async (values: {
		name: string
		slug: string
	}) => {
		setSubmitting(true)
		try {
			const created = await createCarrierType(values)
			setTypes((prev) => [...prev, created])
			message.success('Carrier type created')
			setModalOpen(false)
			form.resetFields()
		} catch {
			message.error('Failed to create carrier type')
		} finally {
			setSubmitting(false)
		}
	}

	const columns = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 80
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: 'Slug',
			dataIndex: 'slug',
			key: 'slug',
			render: (slug: string) => <Tag>{slug}</Tag>
		}
	]

	return (
		<div className="carriers">
			<header className="page-header">
				<Title level={4} style={{ margin: 0 }}>
					Carrier Types
				</Title>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setModalOpen(true)}>
					Add Type
				</Button>
			</header>

			<main>
				<Table
					dataSource={types}
					columns={columns}
					rowKey="id"
					loading={loading}
					pagination={false}
				/>
			</main>

			<Modal
				title="Add Carrier Type"
				open={modalOpen}
				onCancel={() => {
					setModalOpen(false)
					form.resetFields()
				}}
				footer={null}
				destroyOnClose>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleCreate}>
					<Form.Item
						label="Name"
						name="name"
						rules={[{ required: true, message: 'Name is required' }]}>
						<Input placeholder="e.g. General 3000V" />
					</Form.Item>
					<Form.Item
						label="Slug"
						name="slug"
						rules={[
							{ required: true, message: 'Slug is required' },
							{
								pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
								message:
									'Slug must be lowercase-hyphenated, e.g. general-3000v'
							}
						]}>
						<Input placeholder="e.g. general-3000v" />
					</Form.Item>
					<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
						<Button
							onClick={() => {
								setModalOpen(false)
								form.resetFields()
							}}>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={submitting}>
							Create
						</Button>
					</Space>
				</Form>
			</Modal>
		</div>
	)
}

export default UpdatesCarrierTypes
